import React, { useEffect, useState } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "pages/components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/DashboardNavbar";
import {
  Box,
  Grid,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import companyLogo from "assets/images/logos/flex2logo.png";
import { invoiceTableStyle, pdfStyle } from "../style";
import { AdminQuotationApiService } from "api/adminQuotation.api";
import { INVOICE_STATUS_LABELS } from "config/statusLabels";
import { QUOTES_LIST_ROUTE } from "pages/Quotes/utils/routes";

// A flat line carries its own term — a length or a fixed finish — snapshotted
// from the plan when it was quoted.
const subscriptionTermText = (item) => {
  if (item?.subscriptionTerm !== "flat") return "";
  if (item?.subscriptionTermMonths) {
    const m = item.subscriptionTermMonths;
    return m % 12 === 0
      ? ` · ${m / 12} ${m / 12 === 1 ? "year" : "years"}`
      : ` · ${m} ${m === 1 ? "month" : "months"}`;
  }
  if (item?.subscriptionEndDate)
    return ` · until ${String(item.subscriptionEndDate).slice(0, 10)}`;
  return "";
};

const statusColorMap = {
  0: { bgcolor: "#f3f4f6", color: "#6b7280", borderRadius: 6 },
  1: { bgcolor: "#f3f4f6", color: "#6b7280", borderRadius: 6 },
  2: { bgcolor: "#fef9c3", color: "#854d0e", borderRadius: 6 },
  3: { bgcolor: "#dcfce7", color: "#15803d", borderRadius: 6 },
  4: { bgcolor: "#fee2e2", color: "#b91c1c", borderRadius: 6 },
  5: { bgcolor: "#fef9c3", color: "#854d0e", borderRadius: 6 },
};

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const formatDate = (value) => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString();
};

const renderAddressLines = (address = {}, fallbackName = "-") => {
  const name = [address.firstName, address.lastName].filter(Boolean).join(" ") || address.name;
  const lines = [
    name || fallbackName,
    address.addressLineOne,
    address.addressLineTwo,
    [address.city, address.state, address.zipCode].filter(Boolean).join(", "),
    address.country,
  ].filter(Boolean);

  if (lines.length === 0) return ["-"];
  return lines;
};

const getCustomerName = (quote) => {
  if (quote?.customerName) return quote.customerName;
  const fullName = [quote?.billingAddress?.firstName, quote?.billingAddress?.lastName]
    .filter(Boolean)
    .join(" ");
  return fullName || quote?.billingAddress?.name || "-";
};

const QuoteDetails = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadQuote = async () => {
      try {
        setLoading(true);
        const response = await AdminQuotationApiService.getQuotation(quoteId);
        setQuote(response?.data?.data ?? null);
      } catch (error) {
        console.error(error);
        setQuote(null);
      } finally {
        setLoading(false);
      }
    };

    if (quoteId) {
      loadQuote();
    }
  }, [quoteId]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3}>
          <SoftBox sx={pdfStyle}>
            <SoftTypography variant="h5">Loading quotation...</SoftTypography>
          </SoftBox>
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!quote) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3}>
          <SoftBox sx={pdfStyle}>
            <SoftTypography variant="h5" mb={2}>
              Quote not found
            </SoftTypography>
            <SoftButton
              variant="outlined"
              color="info"
              onClick={() => navigate(QUOTES_LIST_ROUTE)}
            >
              Back to Quotations
            </SoftButton>
          </SoftBox>
        </SoftBox>
      </DashboardLayout>
    );
  }

  const customerName = getCustomerName(quote);
  const products = Array.isArray(quote.items) ? quote.items : [];
  const subtotal = products.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1),
    0
  );
  const downPayment = Number(quote.downPayment || 0);
  const paidToDate = Number(quote.paidToDate || 0);
  const docTotal = Number(quote.amount || subtotal);
  const balanceDue = Math.max(docTotal - downPayment - paidToDate, 0);
  const billingLines = renderAddressLines(quote.billingAddress, customerName);
  const shippingLines = renderAddressLines(quote.shippingAddress, customerName);
  const notes =
    quote.note || quote.notes || quote.shippingRemarks || quote.chequeDescription || "-";
  const statusLabel =
    INVOICE_STATUS_LABELS.find((entry) => Number(entry.value) === Number(quote.status))?.label ||
    `Status ${quote.status}`;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox sx={pdfStyle}>
          <Grid container justifyContent="space-between" alignItems="flex-start" mb={2} spacing={2}>
            <Grid item xs={12} md={5}>
              <Box>
                <img src={companyLogo} alt="Company Logo" style={{ width: 130, height: "auto" }} />
                <SoftTypography variant="h6" fontWeight="bold">
                  Flex2 IIOT Data Integrations
                </SoftTypography>
                <SoftTypography variant="body2" fontSize="small" color="text.secondary">
                  416 Market Street Suite 202 Lewisburg, PA 17837
                </SoftTypography>
                <SoftTypography variant="body2" fontSize="small" color="text.secondary">
                  T: 610-572-1955 | E: tony@flex2.io
                </SoftTypography>
              </Box>
            </Grid>

            <Grid item xs={12} md={7} textAlign={{ xs: "left", md: "right" }}>
              <SoftTypography variant="h5" fontWeight="bold" mb={1}>
                Sales Quotation #{quote.id}
              </SoftTypography>
              <SoftBox mb={1} display="flex" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                <SoftBox
                  px={2}
                  py={0.2}
                  width={"fit-content"}
                  borderRadius={1}
                  fontSize="small"
                  fontWeight="bold"
                  textAlign="center"
                  sx={statusColorMap[Number(quote.status)] || statusColorMap[0]}
                >
                  {statusLabel}
                </SoftBox>
              </SoftBox>
              <Table size="small" sx={invoiceTableStyle}>
                <TableBody>
                  <TableRow>
                    <TableCell className="title-cell">SO No</TableCell>
                    <TableCell className="title-cell">Quote Date</TableCell>
                    <TableCell className="title-cell">Due Date</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="value-cell">{quote.soNumber || "-"}</TableCell>
                    <TableCell className="value-cell">{formatDate(quote.createdAt)}</TableCell>
                    <TableCell className="value-cell">{formatDate(quote.dueDate)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="title-cell">Customer PO No.</TableCell>
                    <TableCell className="title-cell">Payment Terms</TableCell>
                    <TableCell className="title-cell">Payment Mode</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="value-cell">{quote.customerPoNumber || "-"}</TableCell>
                    <TableCell className="value-cell">{quote.paymentTerms || "-"}</TableCell>
                    <TableCell className="value-cell">{quote.paymentMode || "-"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="title-cell">Tracking No.</TableCell>
                    <TableCell className="title-cell">Ship Via</TableCell>
                    <TableCell className="title-cell">Shipping Acct. No.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="value-cell">{quote.trackingNumber || "-"}</TableCell>
                    <TableCell className="value-cell">{quote.shipVia || "-"}</TableCell>
                    <TableCell className="value-cell">{quote.shippingAccNumber || "-"}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SoftTypography variant="subtitle2" fontWeight="bold" mb={1}>
                Bill To
              </SoftTypography>
              {billingLines.map((line) => (
                <SoftTypography key={`billing-${line}`} variant="body2">
                  {line}
                </SoftTypography>
              ))}
              {quote.customerEmail && <SoftTypography variant="body2">{quote.customerEmail}</SoftTypography>}
            </Grid>
            <Grid item xs={12} md={6}>
              <SoftTypography variant="subtitle2" fontWeight="bold" mb={1}>
                Ship To
              </SoftTypography>
              {shippingLines.map((line) => (
                <SoftTypography key={`shipping-${line}`} variant="body2">
                  {line}
                </SoftTypography>
              ))}
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={4}>
              <SoftTypography variant="caption" color="text.secondary">
                Customer
              </SoftTypography>
              <SoftTypography variant="body2" fontWeight="medium">
                {customerName || "-"}
              </SoftTypography>
            </Grid>
            <Grid item xs={12} md={4}>
              <SoftTypography variant="caption" color="text.secondary">
                Updated At
              </SoftTypography>
              <SoftTypography variant="body2" fontWeight="medium">
                {formatDate(quote.updatedAt)}
              </SoftTypography>
            </Grid>
          </Grid>

          <TableContainer sx={{ overflowX: "auto", boxShadow: 0, borderRadius: 0 }}>
            <Table size="small" className="c-table">
              <thead className="c-table-head">
                <tr>
                  <th className="c-table-p-0" align="left">
                    <SoftTypography variant="subtitle2" fontWeight="bold">
                      Item
                    </SoftTypography>
                  </th>
                  <th className="c-table-p-0" align="left">
                    <SoftTypography variant="subtitle2" fontWeight="bold">
                      Description
                    </SoftTypography>
                  </th>
                  <th className="c-table-p-0" align="left">
                    <SoftTypography variant="subtitle2" fontWeight="bold">
                      Quantity
                    </SoftTypography>
                  </th>
                  <th className="c-table-p-0" align="center">
                    <SoftTypography variant="subtitle2" fontWeight="bold">
                      Labour Cost
                    </SoftTypography>
                  </th>
                  <th className="c-table-p-0" align="right">
                    <SoftTypography variant="subtitle2" fontWeight="bold">
                      Price
                    </SoftTypography>
                  </th>
                </tr>
              </thead>

              <TableBody>
                {products.map((item, index) => (
                  <TableRow key={item.id || `${item.name}-${index}`}>
                    <TableCell sx={{ p: 0.5 }}>
                      <SoftTypography variant="body2">
                        {item.itemNo || item.id || item.templateId || `ITEM-${index + 1}`}
                      </SoftTypography>
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }}>
                      <SoftTypography variant="body2">
                        {item.description || item.name || "-"}
                        {item.lineKind === "subscription" && item.subscriptionTerm
                          ? ` (billed ${item.subscriptionTerm}${subscriptionTermText(item)})`
                          : ""}
                      </SoftTypography>
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }}>
                      <SoftTypography variant="body2">{item.quantity || 1}</SoftTypography>
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }} align="center">
                      <SoftTypography variant="body2">{formatCurrency(item.rate || 0)}</SoftTypography>
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }} align="right">
                      <SoftTypography variant="body2">
                        {formatCurrency(Number(item.price || 0) * Number(item.quantity || 1))}
                      </SoftTypography>
                    </TableCell>
                  </TableRow>
                ))}
                {products.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <SoftTypography variant="body2">
                        No items found for this quotation.
                      </SoftTypography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Box width={{ xs: "100%", md: 320 }}>
              <Grid container>
                <Grid item xs={6}>
                  <SoftTypography variant="body2" color="text.secondary">
                    Subtotal
                  </SoftTypography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <SoftTypography variant="body2">{formatCurrency(subtotal)}</SoftTypography>
                </Grid>

                <Grid item xs={6} sx={{ mt: 1 }}>
                  <SoftTypography variant="body2" color="text.secondary">
                    Tax
                  </SoftTypography>
                </Grid>
                <Grid item xs={6} sx={{ mt: 1 }} textAlign="right">
                  <SoftTypography variant="body2">{formatCurrency(0)}</SoftTypography>
                </Grid>

                <Grid item xs={6} sx={{ mt: 1 }}>
                  <SoftTypography variant="body2" color="text.secondary">
                    Down Payment
                  </SoftTypography>
                </Grid>
                <Grid item xs={6} sx={{ mt: 1 }} textAlign="right">
                  <SoftTypography variant="body2">{formatCurrency(downPayment)}</SoftTypography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1.5 }} />
                </Grid>

                <Grid item xs={6}>
                  <SoftTypography variant="subtitle2" fontWeight="bold">
                    Doc Total
                  </SoftTypography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <SoftTypography variant="subtitle2" fontWeight="bold">
                    {formatCurrency(docTotal)}
                  </SoftTypography>
                </Grid>

                <Grid item xs={6} sx={{ mt: 1 }}>
                  <SoftTypography variant="body2" color="text.secondary">
                    Paid To Date
                  </SoftTypography>
                </Grid>
                <Grid item xs={6} sx={{ mt: 1 }} textAlign="right">
                  <SoftTypography variant="body2">{formatCurrency(paidToDate)}</SoftTypography>
                </Grid>

                <Grid item xs={6}>
                  <SoftTypography variant="subtitle2" fontWeight="bold">
                    Balance Due
                  </SoftTypography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <SoftTypography variant="subtitle2" fontWeight="bold">
                    {formatCurrency(balanceDue)}
                  </SoftTypography>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <SoftTypography variant="subtitle2" fontWeight="bold" mb={1}>
                Note
              </SoftTypography>
              <SoftTypography variant="body2" color="text.secondary">
                {notes}
              </SoftTypography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <SoftBox display="flex" gap={1} justifyContent="flex-end">
            <SoftButton
              variant="outlined"
              color="info"
              onClick={() => navigate(QUOTES_LIST_ROUTE)}
            >
              Back
            </SoftButton>
            <SoftButton
              variant="outlined"
              color="secondary"
              onClick={() => navigate(`/admin/quotations/${quote.id}/details`)}
            >
              View Details
            </SoftButton>
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </DashboardLayout>
  );
};

export default QuoteDetails;
